
import { supabase } from "@/lib/supabase";

export const updatePackageHours = async (clientId: string, eventId: string, hoursToAdd: number) => {
  try {
    console.log(`Aggiornamento delle ore del pacchetto per il cliente ${clientId} e l'evento ${eventId}`);
    
    const { data: eventData, error: eventError } = await supabase
      .from("calendar_events")
      .select("*")
      .eq("id", eventId)
      .single();
    
    if (eventError) {
      console.error("Errore nel recupero dei dettagli dell'evento:", eventError);
      return;
    }
    
    const { data: sessionData, error: sessionError } = await supabase
      .from("client_packages")
      .select("*, packages(id, name, hours)")
      .eq("client_id", clientId)
      .order("purchase_date", { ascending: false })
      .limit(1);
    
    if (sessionError || !sessionData || sessionData.length === 0) {
      console.error("Errore nel recupero del pacchetto del cliente:", sessionError);
      return;
    }
    
    const packageId = sessionData[0].id;
    const currentHoursUsed = sessionData[0].hours_used || 0;
    const newHoursUsed = currentHoursUsed + hoursToAdd;
    const packageName = sessionData[0].packages?.name || "Pacchetto ore";
    
    console.log(`Aggiornamento delle ore usate per il pacchetto ${packageId}: da ${currentHoursUsed} a ${newHoursUsed}`);
    
    const { data, error } = await supabase
      .from("client_packages")
      .update({ hours_used: newHoursUsed })
      .eq("id", packageId)
      .select();
    
    if (error) {
      console.error("Errore nell'aggiornamento delle ore usate:", error);
      return;
    }
    
    console.log("Ore usate aggiornate con successo:", data);
    
    const { error: notificationError } = await supabase
      .from("notifications")
      .insert({
        type: "package_hours_updated",
        client_id: clientId,
        message: `Sono state utilizzate ${hoursToAdd} ore dal pacchetto per una sessione.`,
        read: false
      });
    
    if (notificationError) {
      console.error("Errore nella creazione della notifica:", notificationError);
    }
    
    // Invio email al cliente
    try {
      const { data: clientData, error: clientError } = await supabase
        .from("clients")
        .select("email, first_name, last_name")
        .eq("id", clientId)
        .single();
      
      if (clientError || !clientData?.email) {
        console.error("Errore nel recupero dei dati del cliente per l'email:", clientError);
        return;
      }
      
      const clientName = `${clientData.first_name} ${clientData.last_name}`;
      const remainingHours = sessionData[0].packages?.hours 
                            ? sessionData[0].packages.hours - newHoursUsed 
                            : undefined;
      
      // Importa le utilità per l'email
      const { sendEmail, createPackageHoursAddedEmail } = await import("@/lib/email-utils");
      
      // Converti la data dell'evento
      const sessionDate = eventData ? new Date(eventData.start_time) : undefined;
      
      const emailHtml = createPackageHoursAddedEmail(
        clientName, 
        packageName, 
        hoursToAdd, 
        sessionDate, 
        remainingHours
      );
      const emailSubject = "Ore utilizzate dal tuo pacchetto";
      
      await sendEmail(clientData.email, emailSubject, emailHtml);
    } catch (emailError) {
      console.error("Errore nell'invio dell'email per aggiornamento ore pacchetto:", emailError);
    }
    
  } catch (error) {
    console.error("Errore generale nell'aggiornamento delle ore del pacchetto:", error);
  }
};

export const returnPackageHours = async (clientId: string, eventId: string, hoursToReturn: number) => {
  try {
    console.log(`Restituzione di ${hoursToReturn} ore al pacchetto per il cliente ${clientId}`);
    
    // Recupera informazioni sull'evento per ottenere la data e l'ora
    const { data: eventData, error: eventError } = await supabase
      .from("calendar_events")
      .select("*")
      .eq("id", eventId)
      .single();
    
    if (eventError) {
      console.error("Errore nel recupero dei dettagli dell'evento:", eventError);
    }
    
    const { data: sessionData, error: sessionError } = await supabase
      .from("client_packages")
      .select("*, packages(id, name, hours)")
      .eq("client_id", clientId)
      .order("purchase_date", { ascending: false })
      .limit(1);
    
    if (sessionError || !sessionData || sessionData.length === 0) {
      console.error("Errore nel recupero del pacchetto del cliente:", sessionError);
      return;
    }
    
    const packageId = sessionData[0].id;
    const currentHoursUsed = sessionData[0].hours_used || 0;
    const packageName = sessionData[0].packages?.name || "Pacchetto ore";
    
    const newHoursUsed = Math.max(0, currentHoursUsed - hoursToReturn);
    
    console.log(`Aggiornamento delle ore usate per il pacchetto ${packageId}: da ${currentHoursUsed} a ${newHoursUsed}`);
    
    const { data, error } = await supabase
      .from("client_packages")
      .update({ hours_used: newHoursUsed })
      .eq("id", packageId)
      .select();
    
    if (error) {
      console.error("Errore nell'aggiornamento delle ore usate:", error);
      return;
    }
    
    console.log("Ore usate aggiornate con successo:", data);
    
    const { error: notificationError } = await supabase
      .from("notifications")
      .insert({
        type: "package_hours_returned",
        client_id: clientId,
        message: `Sono state restituite ${hoursToReturn} ore al pacchetto a seguito dell'eliminazione di una sessione.`,
        read: false
      });
    
    if (notificationError) {
      console.error("Errore nella creazione della notifica:", notificationError);
    }
    
    // Invio email al cliente
    try {
      const { data: clientData, error: clientError } = await supabase
        .from("clients")
        .select("email, first_name, last_name")
        .eq("id", clientId)
        .single();
      
      if (clientError || !clientData?.email) {
        console.error("Errore nel recupero dei dati del cliente per l'email:", clientError);
        return;
      }
      
      const clientName = `${clientData.first_name} ${clientData.last_name}`;
      const remainingHours = sessionData[0].packages?.hours 
                            ? sessionData[0].packages.hours - newHoursUsed 
                            : undefined;
      
      // Importa le utilità per l'email
      const { sendEmail, createPackageHoursRemovedEmail } = await import("@/lib/email-utils");
      
      // Converti la data dell'evento
      const sessionDate = eventData ? new Date(eventData.start_time) : undefined;
      
      const emailHtml = createPackageHoursRemovedEmail(
        clientName, 
        packageName, 
        hoursToReturn,
        sessionDate,
        remainingHours
      );
      const emailSubject = "Ore restituite al tuo pacchetto";
      
      await sendEmail(clientData.email, emailSubject, emailHtml);
    } catch (emailError) {
      console.error("Errore nell'invio dell'email per restituzione ore pacchetto:", emailError);
    }
    
  } catch (error) {
    console.error("Errore generale nella restituzione delle ore al pacchetto:", error);
  }
};
